"use client";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import { ChevronLeft, ChevronRight, Menu } from "lucide-react";
import Image from "next/image";
import NavbarDropdown from "./navbar-dropdown";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useState } from "react";
import logo from "@/public/images/logo.svg";
import { useLocale, useTranslations } from "next-intl";
import DefaultLink from "next/link";
import { unitTypes } from "@/lib/enums";
import LanguageSwitch from "../LanguageSwitch";
import Link from "next/link";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useSearchParams } from "next/navigation";

const NavbarDrawer = () => {
  const t = useTranslations();
  const locale = useLocale();
  const { isLoggedIn } = useSelector((state: RootState) => state.login);
  const [open, setOpen] = useState(false);
  const searchParams = useSearchParams();

  const handleClose = () => setOpen(false);

  return (
    <Drawer
      direction={locale === "ar" ? "left" : "right"}
      {...{ open, onOpenChange: setOpen }}
    >
      <DrawerTrigger asChild>
        <Button aria-label="menu button" variant={"ghost"} size={"icon"}>
          <Menu />
        </Button>
      </DrawerTrigger>
      <DrawerContent
        id="navbar-drawer"
        className="max-w-full w-full *:w-full flex-col py-4 gap-3 bg-white "
      >
        <DrawerTitle className="flex justify-between items-end">
          <DrawerClose className="self-center">
            <ChevronRight className="ltr:rotate-180" />
          </DrawerClose>
          <Link
            onClick={() => setOpen(false)}
            href={"/"}
            className="relative"
            prefetch
          >
            <Image
              width={114}
              height={45}
              alt="logo"
              src={logo}
              className="w-auto h-auto"
            />
          </Link>
          <LanguageSwitch />
        </DrawerTitle>
        {isLoggedIn && <NavbarDropdown setOpen={setOpen} />}
        <div className="space-y-3 *:border-b *:pb-4 mt-6 *:flex *:justify-between *:items-center">
          <Link
            href={{
              pathname: `/all-units`,
              query: {
                classification: unitTypes.hut,
              },
            }}
            onClick={handleClose}
            prefetch
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.hut,
            })}
          >
            {t("navbar.items.woodenCabins")}
            <ChevronLeft className="ltr:rotate-180" />
          </Link>
          <Link
            href={{
              pathname: `/all-units`,
              query: {
                classification: unitTypes.resort,
              },
            }}
            onClick={handleClose}
            prefetch
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.resort,
            })}
          >
            {t("navbar.items.resort")}
            <ChevronLeft className="ltr:rotate-180" />
          </Link>
          <Link
            href={{
              pathname: `/all-units`,
              query: {
                classification: unitTypes.chalet,
              },
            }}
            onClick={handleClose}
            prefetch
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.chalet,
            })}
          >
            {t("navbar.items.chalets")}
            <ChevronLeft className="ltr:rotate-180" />
          </Link>
          <Link
            href={{
              pathname: `/all-units`,
              query: {
                classification: unitTypes.apartment,
              },
            }}
            onClick={handleClose}
            prefetch
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.apartment,
            })}
          >
            {t("navbar.items.furnishedApartments")}
            <ChevronLeft className="ltr:rotate-180" />
          </Link>
          <Link
            href={{
              pathname: `/all-units`,
              query: {
                classification: unitTypes.room,
              },
            }}
            onClick={handleClose}
            prefetch
            className={cn({
              "underline text-green-main underline-offset-2":
                searchParams.get("classification") === unitTypes.room,
            })}
          >
            {t("navbar.items.hotelRoom")}
            <ChevronLeft className="ltr:rotate-180" />
          </Link>
          {process.env.NEXT_PUBLIC_BUSINESS_URL && (
            <DefaultLink
              href={process.env.NEXT_PUBLIC_BUSINESS_URL}
              prefetch
            >
              {t("navbar.items.addYourProperty")}
              <ChevronLeft className="ltr:rotate-180" />
            </DefaultLink>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default NavbarDrawer;
